import { useQuery } from "@apollo/client";

import { ITEMS_PER_PAGE } from "#/src/constants";
import {
  LIST_OEM_INVENTORY_PARTS_QUERY,
  GET_INVENTORY_PART,
  GET_INVENTORY_PART_DETAIL,
  LIST_STOCK_OPERATIONS_QUERY,
} from "~/api";

// all parts under OEM

export const useGetAllParts = ({
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery,
  where = null,
  sort = [],
  skipCondition = false,
} = {}) => {
  const variables = {
    params: {
      limit,
      skip,
      sort,
      where: {
        searchQuery,
        ...(where || {}),
      },
    },
  };

  const {
    data: { listOwnOemInventoryPart = [] } = {},
    loading: fetchingParts,
    refetch: refetchParts,
    fetchMore,
  } = useQuery(LIST_OEM_INVENTORY_PARTS_QUERY, {
    variables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip: skipCondition,
  });

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    parts = [],
  } = listOwnOemInventoryPart;

  const handleFetchMore = ({ limit, skip }) => {
    fetchMore({
      variables: {
        params: {
          limit,
          skip,
          sort,
          where: {
            searchQuery,
            ...(where || {}),
          },
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedParts = [
          ...previousResult.listOwnOemInventoryPart.parts,
          ...fetchMoreResult.listOwnOemInventoryPart.parts,
        ];

        const updates = {
          ...previousResult,
          listOwnOemInventoryPart: {
            ...previousResult.listOwnOemInventoryPart,
            ...fetchMoreResult.listOwnOemInventoryPart,
            parts: mergedParts,
          },
        };

        return updates;
      },
    });
  };

  return {
    parts,
    handleFetchMore,
    refetchParts,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    fetchingParts,
  };
};

export const useGetInventoryPart = (id) => {
  const {
    data: { getInventoryPart } = {},
    loading,
    refetch,
  } = useQuery(GET_INVENTORY_PART, {
    variables: {
      id,
    },
    skip: !id,
  });

  return {
    loading,
    inventoryPart: getInventoryPart,
    refetchInventoryPart: refetch,
  };
};

export const useGetInventoryPartDetail = (id) => {
  const {
    data: { inventoryPart } = {},
    loading,
    refetch,
  } = useQuery(GET_INVENTORY_PART_DETAIL, {
    variables: {
      id,
    },
    skip: !id,
  });

  return {
    loading,
    inventoryPart,
    refetchInventoryPart: refetch,
  };
};

export const useGetStockOperations = ({
  partId,
  operationType = "ADD",
  allocationStatus,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  sort = [],
  skipCondition = false,
} = {}) => {
  const variables = {
    params: {
      partId,
      operationType,
      ...(allocationStatus && { allocationStatus }),
      limit,
      skip,
      sort,
    },
  };

  const {
    data: { listStockOperations = {} } = {},
    loading,
    refetch,
    fetchMore,
  } = useQuery(LIST_STOCK_OPERATIONS_QUERY, {
    variables,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip: !partId || skipCondition,
  });

  const {
    operations = [],
    totalCount,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
  } = listStockOperations;

  const handleFetchMore = ({ limit: newLimit, skip: newSkip }) => {
    fetchMore({
      variables: {
        params: {
          partId,
          operationType,
          ...(allocationStatus && { allocationStatus }),
          limit: newLimit,
          skip: newSkip,
          sort,
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedOperations = [
          ...previousResult.listStockOperations.operations,
          ...fetchMoreResult.listStockOperations.operations,
        ];

        return {
          ...previousResult,
          listStockOperations: {
            ...fetchMoreResult.listStockOperations,
            operations: mergedOperations,
          },
        };
      },
    });
  };

  return {
    stockOperations: operations,
    totalCount,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    loading,
    refetch,
    handleFetchMore,
  };
};

// Backward compatibility exports
export const getAllParts = useGetAllParts;
export const getInventoryPart = useGetInventoryPart;
export const getInventoryPartDetail = useGetInventoryPartDetail;
export const getStockOperations = useGetStockOperations;
